/**
 * React Query хуки справочника должностей (задача 123 §3.1) — по образцу
 * `useAdminEquipment`. Две оси: сам каталог (CRUD + reorder) и набор работ
 * выбранной должности (`replace-all` одним PUT).
 *
 * Набор работ читает только админ; список должностей доступен и руководителю
 * (пикер в диалоге найма, §4.1) — поэтому 403 не ретраим и оставляем пустой
 * список.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/api/client";
import {
  createPosition,
  deletePosition,
  getPositionWorkTypeIds,
  listPositions,
  patchPosition,
  reorderPositions,
  setPositionWorkTypeIds,
  type CreatePositionRequest,
  type PatchPositionRequest,
  type Position,
} from "@/api/positions";

function noRetryOn401(failureCount: number, error: Error): boolean {
  if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
    return false;
  }
  return failureCount < 1;
}

export const POSITIONS_KEY = ["positions"] as const;

export function usePositions(enabled: boolean = true) {
  return useQuery<Position[], Error>({
    queryKey: POSITIONS_KEY,
    queryFn: listPositions,
    retry: noRetryOn401,
    staleTime: 60_000,
    enabled,
  });
}

export function useCreatePosition() {
  const qc = useQueryClient();
  return useMutation<Position, Error, CreatePositionRequest>({
    mutationFn: createPosition,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: POSITIONS_KEY });
    },
  });
}

export interface PatchPositionVars {
  id: number;
  body: PatchPositionRequest;
}

export function usePatchPosition() {
  const qc = useQueryClient();
  return useMutation<Position, Error, PatchPositionVars>({
    mutationFn: ({ id, body }) => patchPosition(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: POSITIONS_KEY });
    },
  });
}

export function useDeletePosition() {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: deletePosition,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: POSITIONS_KEY });
    },
  });
}

export function useReorderPositions() {
  const qc = useQueryClient();
  return useMutation<void, Error, number[]>({
    mutationFn: (orderedIds) => reorderPositions(orderedIds),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: POSITIONS_KEY });
    },
  });
}

export const POSITION_WORK_TYPES_KEY = (positionId: number) =>
  ["positions", positionId, "work-types"] as const;

export function usePositionWorkTypes(positionId: number | null) {
  return useQuery<number[], Error>({
    queryKey:
      positionId == null
        ? (["positions", "_none", "work-types"] as const)
        : POSITION_WORK_TYPES_KEY(positionId),
    queryFn: () => {
      if (positionId == null) return Promise.resolve([]);
      return getPositionWorkTypeIds(positionId);
    },
    enabled: positionId != null,
    retry: noRetryOn401,
    staleTime: 30_000,
  });
}

export interface SetPositionWorkTypesVars {
  positionId: number;
  ids: number[];
}

export function useSetPositionWorkTypes() {
  const qc = useQueryClient();
  return useMutation<number[], Error, SetPositionWorkTypesVars>({
    mutationFn: ({ positionId, ids }) => setPositionWorkTypeIds(positionId, ids),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: POSITION_WORK_TYPES_KEY(vars.positionId) });
    },
  });
}
